import cx from 'classnames';
import {
  memo,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FunctionComponent,
} from 'react';

import type { SourceType } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';
import {
  ChunkLoadErrorBoundary,
  ErrorBoundary,
} from '@trello/error-boundaries';
import { useFeatureGate } from '@trello/feature-gate-client';
import {
  BoardIdProvider,
  EnterpriseIdProvider,
  ListIdProvider,
  WorkspaceIdProvider,
} from '@trello/id-context';
import { Layers } from '@trello/layer-manager';
import { Dialog, useDialog } from '@trello/nachos/dialog';
import { useSharedState } from '@trello/shared-state';
import { useLazyComponent } from '@trello/use-lazy-component';

import { ModelLoader } from 'app/scripts/db/model-loader';
import type { Board } from 'app/scripts/models/Board';
import type { Card } from 'app/scripts/models/Card';
import { BoardMembersContextProvider } from 'app/src/components/BoardMembersContext';
import { BoardPermissionsContextProvider } from 'app/src/components/BoardPermissionsContext';
import { BoardPluginsContextProvider } from 'app/src/components/BoardPluginsContext';
import { noop } from 'app/src/noop';
import { CARD_BACK_NAME_ID_ATTRIBUTE } from './CardBack.constants';
import type { CardBackKeyboardShortcutListenerProps } from './CardBackKeyboardShortcutListener';
import { CardBackLoading } from './CardBackLoading';
import { CardBackLoadingError } from './CardBackLoadingError';
import { cardBackSharedState } from './cardBackSharedState';
import { isCardBackFocusTrapDisabledState } from './isCardBackFocusTrapDisabledState';
import { useIsCardBackRedesignEnabled } from './useIsCardBackRedesignEnabled';

import styles from './CardBackDialogWithoutRouting.less';

interface CardBackDialogWithoutRoutingProps
  extends CardBackKeyboardShortcutListenerProps {
  boardId: string;
  cardId: string;
  onClose: () => void;
  source: SourceType;
  mirrorCardBoardId?: string;
}

export const CardBackDialogWithoutRouting: FunctionComponent<CardBackDialogWithoutRoutingProps> =
  memo(
    ({
      boardId,
      cardId,
      onClose,
      source,
      mirrorCardBoardId,
      getPreviousCard,
      getNextCard,
      getPreviousList,
      getNextList,
    }) => {
      const { value: isReactFocusLockEnabled } = useFeatureGate(
        'ghost_use_react_focus_lock',
      );
      const { value: isAnimationEnabled } = useFeatureGate(
        'phx_card_back_animations',
      );

      const [board, setBoard] = useState<Board | null>(null);
      const [card, setCard] = useState<Card | null>(null);
      const [traceId, setTraceId] = useState<string | null>(null);

      const { dialogProps } = useDialog();
      const dialogRef = useRef<HTMLDivElement | null>(null);

      const isRedesignEnabled = useIsCardBackRedesignEnabled();
      const [{ isPanelOpen }] = useSharedState(cardBackSharedState);

      const [isFocusTrappingDisabled] = useSharedState(
        isCardBackFocusTrapDisabledState,
      );

      const CardBack = useLazyComponent(
        () => import(/* webpackChunkName: "card-back" */ './CardBack'),
        { preload: false, namedImport: 'CardBack' },
      );

      const onChunkLoadError = useCallback(() => {
        if (traceId) {
          Analytics.taskFailed({
            taskName: 'view-card/withoutRouting',
            source,
            traceId,
            error: 'OCA - Chunk load error',
          });
        }
      }, [source, traceId]);

      const onError = useCallback(
        (error: Error) => {
          if (traceId) {
            Analytics.taskFailed({
              taskName: 'view-card/withoutRouting',
              source,
              traceId,
              error: 'OCA - ' + error,
            });
          }
        },
        [source, traceId],
      );

      const onCompleteTask = useCallback(() => {
        if (traceId) {
          Analytics.taskSucceeded({
            taskName: 'view-card/withoutRouting',
            source,
            traceId,
          });

          setTraceId(null);
        }
      }, [source, traceId]);

      useEffect(() => {
        const newTraceId = Analytics.startTask({
          taskName: 'view-card/withoutRouting',
          source,
        });
        setTraceId(newTraceId);
      }, [source]);

      // We're using the legacy ModelLoader because some parts of the card back
      // still use the legacy ModelCache - attachments, power ups - through the
      // use of useLegacyCardModel. There is no way right now to sync the Apollo
      // cache to ModelCache so we will use ModelLoader until those are refactored.
      useEffect(() => {
        const loadData = async () => {
          const cardPromise = ModelLoader.loadCardData(cardId);
          const boardPromise = ModelLoader.loadCurrentBoardInfo(boardId);
          const boardListsCardsPromise =
            ModelLoader.loadCurrentBoardListsCards(boardId);

          const [cardData, boardData] = await Promise.all([
            cardPromise,
            boardPromise,
            boardListsCardsPromise,
          ]);
          setCard(cardData);
          setBoard(boardData);
        };

        if (boardId && cardId) {
          loadData();
        }
      }, [boardId, cardId]);

      if (!card || !board) {
        return null;
      }

      return (
        <EnterpriseIdProvider value={board.attributes.idEnterprise}>
          <WorkspaceIdProvider value={board.attributes.idOrganization}>
            <BoardIdProvider value={boardId}>
              <BoardMembersContextProvider>
                <BoardPermissionsContextProvider>
                  <BoardPluginsContextProvider>
                    <ListIdProvider value={card.attributes.idList}>
                      <Dialog
                        {...dialogProps}
                        isOpen={true}
                        className={cx({
                          [styles.cardBackDialog]: true,
                          [styles.cardBackRedesign]: isRedesignEnabled,
                          [styles.animationEnabled]: isAnimationEnabled,
                          [styles.withPanel]: isRedesignEnabled && isPanelOpen,
                        })}
                        size={isRedesignEnabled ? 'medium' : 'large'}
                        hide={onClose}
                        dangerous_disableFocusTrapping={
                          isReactFocusLockEnabled
                            ? undefined
                            : isFocusTrappingDisabled
                        }
                        layer={Layers.CardBack}
                        labelledBy={CARD_BACK_NAME_ID_ATTRIBUTE}
                        ref={dialogRef}
                      >
                        <ErrorBoundary
                          tags={{
                            ownershipArea: 'trello-web-eng',
                            feature: 'Card Detail Dialog Without Routing',
                          }}
                          onError={onError}
                        >
                          <ChunkLoadErrorBoundary
                            fallback={<CardBackLoadingError />}
                            onError={onChunkLoadError}
                          >
                            <Suspense fallback={<CardBackLoading />}>
                              <CardBack
                                cardId={cardId}
                                dangerous_mirrorCardBoardId={mirrorCardBoardId}
                                dialogRef={dialogRef}
                                dismissCardBackDialog={onClose}
                                isOpenedFromSourceBoard={false}
                                openCardBackDialog={noop}
                                getPreviousList={getPreviousList}
                                getNextList={getNextList}
                                getPreviousCard={getPreviousCard}
                                getNextCard={getNextCard}
                                onMount={onCompleteTask}
                              />
                            </Suspense>
                          </ChunkLoadErrorBoundary>
                        </ErrorBoundary>
                      </Dialog>
                    </ListIdProvider>
                  </BoardPluginsContextProvider>
                </BoardPermissionsContextProvider>
              </BoardMembersContextProvider>
            </BoardIdProvider>
          </WorkspaceIdProvider>
        </EnterpriseIdProvider>
      );
    },
  );
